import {
	BigNum,
	Event,
	EventType,
	getAuctionPrice,
	isVariant,
	MarketType,
	OrderAction,
	OrderActionExplanation,
	OrderActionRecord,
	OrderRecord,
	PerpMarketConfig,
	PositionDirection,
	PRICE_PRECISION_EXP,
	PublicKey,
	SpotMarketConfig,
	WrappedEvent,
} from '@drift-labs/sdk';
import {
	ENUM_UTILS,
	matchEnum,
	Serializer,
	UISerializableOrder,
} from '@drift/common';
import { Transaction } from '@solana/web3.js';
import {
	OrderedPerpMarkets,
	OrderedSpotMarkets,
} from 'src/environmentVariables/EnvironmentVariables';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import useDriftActions from 'src/hooks/useDriftActions';
import React, {
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useRef,
} from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { useImmer } from 'use-immer';
import { hideNotification, notify } from 'src/utils/notifications';
import { SlotContext } from './currentSlotProvider';
import { DriftMarketEventSubscriberContext } from './driftMarketEventSubscriberProvider';
import type {
	AuctionOrderSignedTxHandler,
	AuctionToastEventHandler,
} from 'src/utils/DriftAppEvents';
import useOnWalletChange from '../hooks/useOnWalletChange';
import UI_UTILS from 'src/utils/uiUtils';
import { dlog } from '../dev';

// ⭐️ TODO : Add logic to keep action events "buffered" if they come before the order record, instead of skipping them entirely

const DEV_SKIP_ALL_ACTION_EVENTS = false; // Dev switch to see how the auctions handle no action events coming through
const DEV_SKIP_ALL_ORDER_EVENTS = false; // Dev switch to see how the auctions handle no order events coming through

const CLEANUP_AFTER_FINISHED_DELAY = 1000 * 5; // Delay before clearing auction state when we know an order has ended
const CLEANUP_AFTER_TIMEOUT_DELAY = 1000 * 40; // Delay before clearing auction when we assume something has gone wrong

export type AuctionState = {
	duration: number;
	startSlot: number;
	baseAmount: number;
	baseAmountFilled: number;
	quoteAmount: number;
	quoteAmountFilled: number;
	currentPriceEstimate: number;
	market: PerpMarketConfig | SpotMarketConfig;
	order: UISerializableOrder;
	lastAction: OrderAction;
	actionExplanation: OrderActionExplanation;
	finished: boolean;
	auctionEndPrice: number;
	auctionStartPrice: number;
};

type AuctionStateLookup = { [key: string]: AuctionState };

type AuctionContextState = AuctionStateLookup;

const EMPTY_AUCTIONS_LOOKUP = {};

export const AuctionDataContext = React.createContext<AuctionContextState>(
	EMPTY_AUCTIONS_LOOKUP
);

export type AuctionIdentifierProps = {
	marketIndex: number;
	marketType: MarketType;
	direction: PositionDirection;
	orderId: number;
};

const getKeyForAllowedOrderAuction = ({
	marketIndex,
	marketType,
	orderId,
	direction,
}: AuctionIdentifierProps) => {
	return `${marketIndex.toString()}_${ENUM_UTILS.toStr(
		marketType
	)}_${orderId}_${ENUM_UTILS.toStr(direction)}`;
};

const DEFAULT_PREVIOUS_TOAST_ID = 'true';

const AuctionDataProvider = (props: PropsWithChildren<any>) => {
	// # Constants
	const currentSlotContext = useContext(SlotContext).currentSlot;
	const currentSlotRef = useRef(currentSlotContext);
	const appEventEmitter = useAppEventEmitter();
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const setDriftStore = useDriftStore((s) => s.set);
	const expectedAuctions = useRef<Map<string, string>>(new Map());

	const getOraclePrice = (marketIndex: number, marketType: MarketType) =>
		ENUM_UTILS.match(marketType, MarketType.PERP)
			? driftClient.getOracleDataForPerpMarket(marketIndex).price
			: driftClient.getOracleDataForSpotMarket(marketIndex).price;

	const { emitAppEvent } = useDriftActions();

	const checkAuctionExpected = (props: AuctionIdentifierProps) => {
		const key = getKeyForAllowedOrderAuction(props);
		return expectedAuctions.current.get(key);
	};

	const handleNewExpectedAuction = (
		props: AuctionIdentifierProps,
		toastId: string
	) => {
		const key = getKeyForAllowedOrderAuction(props);
		dlog(`stuck_toasts`, `new_expected_auction key: ${key}`);
		expectedAuctions.current.set(key, toastId || DEFAULT_PREVIOUS_TOAST_ID);
	};

	const clearExpectedAuction = (props: AuctionIdentifierProps) => {
		const key = getKeyForAllowedOrderAuction(props);
		expectedAuctions.current.delete(key);
	};

	const [signedFillTxCache, setSignedFillTxCache] = useImmer<
		Record<string, Transaction>
	>({});

	const getStateKey = (marketIndex: string | number, orderId: number) =>
		`${marketIndex}_${orderId}`;

	const nukeAuctionState = useCallback(() => {
		setAuctions(EMPTY_AUCTIONS_LOOKUP);
		cleanupState.current = EMPTY_AUCTIONS_LOOKUP;
	}, []);

	const handleEndOfAuction = (
		marketIndex: number,
		orderId: number,
		marketType: MarketType,
		direction: PositionDirection
	) => {
		const stateKey = getStateKey(marketIndex, orderId);

		dlog(
			`stuck_toasts`,
			`handle_end_of_auction .. key for update : ${stateKey}`
		);

		clearAuctionState(marketIndex, orderId);
		clearExpectedAuction({
			marketIndex,
			marketType,
			direction,
			orderId,
		});
	};

	const addFallbackAuctionStateClearing = (
		marketIndex: number,
		orderId: number,
		marketType: MarketType,
		direction: PositionDirection
	) => {
		setTimeout(() => {
			dlog(`stuck_toasts`, `fallback_auction_state_clearing`);
			handleEndOfAuction(marketIndex, orderId, marketType, direction);
		}, CLEANUP_AFTER_TIMEOUT_DELAY);
	};

	const addToSignedTxCache = (
		orderId: number,
		marketIndex: number,
		tx: Transaction
	) => {
		setSignedFillTxCache((s) => {
			s[getStateKey(marketIndex.toString(), orderId)] = tx;
		});
	};

	const removeFromSignedTxCache = (orderId: number, marketIndex: number) => {
		setSignedFillTxCache((s) => {
			delete s[getStateKey(marketIndex.toString(), orderId)];
		});
	};

	const getFromTxCache = (orderId: number, marketIndex: number) =>
		signedFillTxCache[getStateKey(marketIndex.toString(), orderId)];

	/**
	 * # Fallback self-fill tx handler
	 * This handler catches the self-fill transactions when the user submits an auction order.
	 *
	 * The UI will dispatch this self-filling transaction if it determines that the user's order didn't get filled on its own as exptected.
	 */
	useEffect(() => {
		const handler: AuctionOrderSignedTxHandler = ({
			tx,
			orderId,
			marketIndex,
		}) => {
			addToSignedTxCache(orderId, marketIndex, tx);
		};

		appEventEmitter.on('fallbackAuctionOrderSignedTx', handler);

		return () => {
			appEventEmitter.removeListener('fallbackAuctionOrderSignedTx', handler);
		};
	}, [appEventEmitter]);

	useEffect(() => {
		const handler: AuctionToastEventHandler = (props) => {
			dlog(`stuck_toasts`, `caught_expect_auction_event`, props);
			handleNewExpectedAuction(props.auctionIdentifierProps, props.toastId);
		};

		appEventEmitter.on('expectIncomingAuction', handler);

		return () => {
			appEventEmitter.off('expectIncomingAuction', handler);
		};
	}, [appEventEmitter]);

	useEffect(() => {
		const handler: AuctionToastEventHandler = (props) => {
			dlog(`stuck_toasts`, `cancel_expected_auction`, props);
			clearExpectedAuction(props.auctionIdentifierProps);
		};

		appEventEmitter.on('cancelExpectedAuction', handler);

		return () => {
			appEventEmitter.off('cancelExpectedAuction', handler);
		};
	}, [appEventEmitter]);

	useEffect(() => {
		currentSlotRef.current = currentSlotContext;
	}, [currentSlotContext]);

	// Nuke the auction state whenever the user changes wallets
	useOnWalletChange(nukeAuctionState);

	// # State
	const [auctions, setAuctions] = useImmer<AuctionStateLookup>(
		EMPTY_AUCTIONS_LOOKUP
	);
	const cleanupState = useRef<{ [key: string]: boolean }>(
		EMPTY_AUCTIONS_LOOKUP
	);

	// # State-Dependent constants
	const contextValue = auctions;

	// # Methods
	const clearAuctionState = (marketIndex: number | string, orderId: number) => {
		setAuctions((s) => {
			const stateKey = getStateKey(marketIndex, orderId);

			const entryBeingRemoved = s[stateKey];

			if (entryBeingRemoved) {
				removeFromSignedTxCache(
					entryBeingRemoved.order.orderId,
					entryBeingRemoved.market.marketIndex
				);

				delete s[stateKey];
			}
		});
	};

	const getState = useDriftAccountsStore((s) => s.get);

	const eventSubscriberContext = useContext(DriftMarketEventSubscriberContext);

	const findAccountToAppendTo = (pubKey: PublicKey): PublicKey => {
		if (!pubKey) return undefined;

		const findAcct = Object.values(getState().accounts).find((acct) =>
			acct.pubKey?.equals(pubKey)
		);

		return findAcct?.pubKey;
	};

	/**
	 * If the action record matches one of the users and an order that is already in the auction state then update the auction state with the latest progress
	 * @param event
	 * @returns
	 */
	const handleActionRecord = (event: Event<OrderActionRecord>) => {
		if (DEV_SKIP_ALL_ACTION_EVENTS) return;

		const matchingTaker = findAccountToAppendTo(event.taker);
		const matchingMaker = findAccountToAppendTo(event.maker);

		const isTaker = !!matchingTaker;

		const matchingUserPubKey =
			matchingTaker !== undefined
				? matchingTaker
				: matchingMaker !== undefined
				? matchingMaker
				: undefined;

		// Skip order which doesn't match current user
		if (matchingUserPubKey === undefined) {
			return;
		}

		dlog(`stuck_toasts`, `caught_action_record_event`);

		const serializedActionRecord =
			Serializer.Serialize.OrderActionRecord(event);

		const formattedActionRecord = Serializer.Deserialize.UIOrderActionRecord(
			serializedActionRecord
		);

		const orderIsCancelledOrExpired =
			matchEnum(formattedActionRecord.action, OrderAction.CANCEL) ||
			matchEnum(formattedActionRecord.action, OrderAction.EXPIRE);

		const orderIsFinished =
			orderIsCancelledOrExpired ||
			(isTaker
				? formattedActionRecord.takerOrderCumulativeBaseAssetAmountFilled
					? formattedActionRecord.takerOrderBaseAssetAmount.eq(
							formattedActionRecord.takerOrderCumulativeBaseAssetAmountFilled
					  )
					: false
				: formattedActionRecord.makerOrderCumulativeBaseAssetAmountFilled
				? formattedActionRecord.makerOrderBaseAssetAmount.eq(
						formattedActionRecord.makerOrderCumulativeBaseAssetAmountFilled
				  )
				: false);

		const orderId = isTaker
			? formattedActionRecord.takerOrderId
			: formattedActionRecord.makerOrderId;

		if (orderIsFinished) {
			const eventType = orderIsCancelledOrExpired
				? 'orderCancelled'
				: 'orderFillComplete';

			// We listen to this in usePnlModalDisplayListener to show the PnlModal after an order fills
			// And in TradingViewUtils to remove cancelled order lines from the chart
			emitAppEvent(eventType, {
				isTaker,
				orderId,
				orderActionRecord: formattedActionRecord,
			});
		}

		const auctionIdentifierProps = {
			marketIndex: formattedActionRecord.marketIndex,
			marketType: formattedActionRecord.marketType,
			direction: isTaker
				? formattedActionRecord.takerOrderDirection
				: formattedActionRecord.makerOrderDirection,
			orderId: isTaker
				? formattedActionRecord.takerOrderId ?? 0
				: formattedActionRecord.makerOrderId ?? 0,
		};
		// Skip handling this action record if we're not expecting an auction for it
		if (!checkAuctionExpected(auctionIdentifierProps)) {
			dlog(
				`stuck_toasts`,
				`skipping_update_for_unexpected_action_record .. key for update : ${getKeyForAllowedOrderAuction(
					auctionIdentifierProps
				)}`
			);
			return;
		}

		const tradeInfo = UI_UTILS.getTradeInfoFromOrderActionRecord(
			matchingUserPubKey,
			formattedActionRecord
		);
		const updatedState: Partial<AuctionState> = {
			baseAmountFilled: tradeInfo.totalBaseAmountFilled.toNum(),
			quoteAmountFilled: tradeInfo.totalQuoteAmountFilled?.toNum() ?? 0,
			lastAction: formattedActionRecord.action,
			actionExplanation: formattedActionRecord.actionExplanation,
		};
		const marketIndex = formattedActionRecord.marketIndex;

		const stateKey = getStateKey(marketIndex.toString(), orderId);

		// Update Auction State
		setAuctions((s) => {
			if (!s[stateKey]) {
				dlog(
					`stuck_toasts`,
					`skipping_action_event_update_because_missing_auction_state`
				);
				return;
			}

			s[stateKey] = {
				...s[stateKey],
				...updatedState,
			};
		});

		if (orderIsCancelledOrExpired) {
			if (
				matchEnum(
					formattedActionRecord.actionExplanation,
					OrderActionExplanation.ORACLE_PRICE_BREACHED_LIMIT_PRICE
				)
			) {
				notify({
					type: 'info',
					id: `${stateKey}_price_breach_warning`,
					message: `Couldn't fill order`,
					description: 'Oracle Price Breached Limit Price',
					lengthMs: 8000,
				});
			}

			if (
				matchEnum(
					formattedActionRecord.actionExplanation,
					OrderActionExplanation.LIQUIDATION
				)
			) {
				notify({
					type: 'info',
					id: `${stateKey}_liquidation`,
					message: `Couldn't fill order`,
					description: 'Cancelled For Liquidation',
					lengthMs: 8000,
				});
			}

			if (
				matchEnum(
					formattedActionRecord.actionExplanation,
					OrderActionExplanation.MARKET_EXPIRED
				)
			) {
				notify({
					type: 'info',
					id: `${stateKey}_market_expired`,
					message: `Couldn't fill order`,
					description: 'Market Order Auction Expired',
					lengthMs: 8000,
				});
			}

			if (
				matchEnum(
					formattedActionRecord.actionExplanation,
					OrderActionExplanation.ORDER_EXPIRED
				)
			) {
				notify({
					type: 'info',
					id: `${stateKey}_order_expired`,
					message: `Market Order Expired`,
					description:
						'Try increasing your slippage tolerance, reducing your order size, or placing a limit order.',
					lengthMs: 8000,
				});
				dlog(`stuck_toasts`, `order_expired`);
				handleEndOfAuction(
					marketIndex,
					orderId,
					auctionIdentifierProps.marketType,
					auctionIdentifierProps.direction
				);
				return;
			}
		}

		// If order is finished, clean up after a delay
		if (orderIsFinished) {
			let cleanupTime = CLEANUP_AFTER_FINISHED_DELAY;

			// If the order got cancelled for a reason, double the cleanup time so that user has enough time to read the reason
			if (
				!matchEnum(
					formattedActionRecord.actionExplanation,
					OrderActionExplanation.NONE
				)
			) {
				cleanupTime *= 2;
			}

			dlog(`stuck_toasts`, `order_finished .. key for update : ${stateKey}`);

			if (!cleanupState[stateKey]) {
				setAuctions((s) => {
					if (s[stateKey]) {
						s[stateKey].finished = true;
					}
				});

				cleanupState[stateKey] = true;

				setTimeout(() => {
					handleEndOfAuction(
						marketIndex,
						orderId,
						auctionIdentifierProps.marketType,
						auctionIdentifierProps.direction
					);
				}, cleanupTime);
			}
		}
	};

	/**
	 * If the order record event has an auction and it is for one of the current users then add it to the auction state
	 * @param event
	 * @returns
	 */
	const handleOrderRecord = (event: Event<OrderRecord>) => {
		if (DEV_SKIP_ALL_ORDER_EVENTS) return;

		if (!event.order.auctionDuration || event.order.auctionDuration === 0)
			return;

		const matchingTaker = findAccountToAppendTo(event.user);

		if (matchingTaker === undefined) {
			return;
		}

		const formattedOrderRecord = Serializer.Deserialize.UIOrderRecord(event);

		const auctionIdentifierProps = {
			marketIndex: formattedOrderRecord.order.marketIndex,
			marketType: formattedOrderRecord.order.marketType,
			direction: formattedOrderRecord.order.direction,
			orderId: formattedOrderRecord.order.orderId,
		};

		// Skip handling this action record if we're not expecting an auction for it
		if (!checkAuctionExpected(auctionIdentifierProps)) {
			dlog(
				`stuck_toasts`,
				`skipping_update_for_unexpected_order_record .. key for update : ${getKeyForAllowedOrderAuction(
					auctionIdentifierProps
				)}`
			);
			return;
		}

		dlog(`stuck_toasts`, `caught_order_record_event`);

		const previousToastId = checkAuctionExpected(auctionIdentifierProps);
		if (previousToastId !== DEFAULT_PREVIOUS_TOAST_ID) {
			hideNotification(setDriftStore, previousToastId);
		}

		const currentSlot = currentSlotRef.current;

		const auctionState: AuctionState = {
			duration: event.order.auctionDuration,
			startSlot: event.order.slot.toNumber(),
			baseAmount: formattedOrderRecord.order.baseAssetAmount.toNum(),
			baseAmountFilled:
				formattedOrderRecord.order.baseAssetAmountFilled.toNum(),
			quoteAmount: formattedOrderRecord.order.quoteAssetAmount?.toNum() ?? 0,
			quoteAmountFilled:
				formattedOrderRecord.order.quoteAssetAmountFilled?.toNum() ?? 0,
			currentPriceEstimate: BigNum.from(
				getAuctionPrice(
					event.order,
					currentSlot,
					getOraclePrice(event.order.marketIndex, event.order.marketType)
				),
				PRICE_PRECISION_EXP
			).toNum(),
			market: isVariant(formattedOrderRecord.order.marketType, 'perp')
				? OrderedPerpMarkets[formattedOrderRecord.order.marketIndex]
				: OrderedSpotMarkets[formattedOrderRecord.order.marketIndex],
			order: formattedOrderRecord.order,
			lastAction: OrderAction.PLACE,
			actionExplanation: OrderActionExplanation.NONE,
			auctionEndPrice: formattedOrderRecord.order.auctionEndPrice.toNum(),
			auctionStartPrice: formattedOrderRecord.order.auctionEndPrice.toNum(),
			finished: false,
		};

		const stateKey = getStateKey(
			formattedOrderRecord.order.marketIndex.toString(),
			formattedOrderRecord.order.orderId
		);

		setAuctions((s) => {
			s[stateKey] = auctionState;
		});

		addFallbackAuctionStateClearing(
			formattedOrderRecord.order.marketIndex,
			formattedOrderRecord.order.orderId,
			auctionIdentifierProps.marketType,
			auctionIdentifierProps.direction
		);
	};

	// # Effect Hooks
	//// Keep auction state in sync with open orders state
	useEffect(() => {
		if (
			eventSubscriberContext.ready &&
			eventSubscriberContext.eventSubscriber
		) {
			const updateHandler = (event: WrappedEvent<EventType>) => {
				try {
					if (event.eventType === 'OrderRecord') {
						handleOrderRecord(event as Event<OrderRecord>);
						return;
					}

					if (event.eventType === 'OrderActionRecord') {
						handleActionRecord(event as Event<OrderActionRecord>);
						return;
					}
				} catch (e) {
					console.error('Error in Drift auction listener: ', e);
					return;
				}
			};

			eventSubscriberContext.eventSubscriber.eventEmitter.on(
				'newEvent',
				updateHandler
			);

			return () => {
				eventSubscriberContext.eventSubscriber.eventEmitter.removeListener(
					'newEvent',
					updateHandler
				);
			};
		}
	}, [eventSubscriberContext]);

	// Update price estimates when slot changes
	useEffect(() => {
		const newAuctionState: AuctionStateLookup = { ...auctions };

		Object.keys(auctions).forEach((key) => {
			const auction = newAuctionState[key];

			const serializedOrder = Serializer.Serialize.Order(auction.order);
			const rawOrder = Serializer.Deserialize.Order(serializedOrder);

			newAuctionState[key] = {
				...auction,
				currentPriceEstimate: BigNum.from(
					getAuctionPrice(
						rawOrder,
						currentSlotContext,
						getOraclePrice(rawOrder.marketIndex, rawOrder.marketType)
					),
					PRICE_PRECISION_EXP
				).toNum(),
			};
		});

		if (Object.keys(newAuctionState).length > 0) {
			setAuctions(newAuctionState);
		}
	}, [currentSlotContext]);

	// Handle trying to fill unfilled auctions
	useEffect(() => {
		const newAuctionState: AuctionStateLookup = { ...auctions };

		Object.keys(auctions).forEach((key) => {
			const auction = newAuctionState[key];

			if (
				currentSlotContext >=
				auction.startSlot + auction.duration / 2 /*
			Doing duration/2 to account for lag in slot subscriber
			*/
			) {
				// Check if there is a fill tx we can send
				const signedTx = getFromTxCache(
					auction.order.orderId,
					auction.order.marketIndex
				);

				// Send the signed tx if it exists
				if (signedTx) {
					if (!auction.finished) {
						try {
							signedTx.feePayer = driftClient.authority;
							driftClient.sendSignedTx(signedTx);
						} catch (e) {
							// If there is an error here then the order has probably just already been filled
							if (e.logs) {
								console.error(e.logs);
							}
						}
					}
					removeFromSignedTxCache(
						auction.order.orderId,
						auction.order.marketIndex
					);
				}
			}
		});

		if (Object.keys(newAuctionState).length > 0) {
			setAuctions(newAuctionState);
		}
	}, [currentSlotContext]);

	// # Rendering
	return (
		<AuctionDataContext.Provider value={contextValue}>
			{props.children}
		</AuctionDataContext.Provider>
	);
};

export default AuctionDataProvider;
